import isClientSide from "@/js/helpers/isClientSide";
import { trackEvent } from "@/js/helpers/tracking";

/**
 * Kore DOM id varies by SDK build; match fuel-cart `chatbotHelpers` resolution.
 *
 * @returns {HTMLElement | null}
 */
export const findKoreChatRoot = () => {
  if (!isClientSide()) return null;
  return (
    document.querySelector('[id*="kore-chat-window"]') ||
    document.getElementById("kore-chat-window")
  );
};

/**
 * Whether `event.target` is a click on the Kore **floating launcher** (icon / rail open surface),
 * not the in-window composer. Keeps parity when `.chat-circle` moves or nested targets receive the click.
 *
 * @param {HTMLElement | null} root — Kore shell from `findKoreChatRoot` / `useChat` observer
 * @param {EventTarget | null} target — `event.target`
 * @returns {boolean}
 */
export const isKoreLauncherClickWithinRoot = (root, target) => {
  if (!root || !(target instanceof Element)) return false;
  if (!root.contains(target)) return false;
  if (target.closest('[role="textbox"]')) return false;
  if (target.closest(".chat-circle")) return true;
  if (target.closest('[class*="chat-circle"]')) return true;
  const koreBtn = target.closest(".kore-chat-button");
  if (koreBtn && root.contains(koreBtn)) return true;
  return false;
};

/**
 * `ElementClicked` for opening chat from the launcher — matches warehouse expectations:
 * `elementType` BUTTON, `position` CHAT, `text` Open; `location` is filled by Tagular (`getTagularLocation()`).
 */
export const trackKoreChatLauncherElementClicked = () => {
  trackEvent({
    action: "elementClicked",
    data: {
      elementType: "BUTTON",
      position: "CHAT",
      text: "Open",
    },
  });
};

/**
 * Ensure the floating Kore shell is visible (Redux sync may use a different id and miss the node).
 */
const showKoreChatWindow = () => {
  const root = findKoreChatRoot();
  if (root) {
    root.style.display = "block";
    root.style.visibility = "visible";
  }
};

/** @see fuel-cart `CHATBOT_SELECTORS` — omit script/iframe (not clickable). */
const CHATBOT_CLICK_SELECTORS = [
  '[id*="kore"]',
  '[class*="kore"]',
  "[data-kore-chatbot]",
  ".kore-chat-window",
  ".kore-chat-header",
  ".kore-chat-button",
  "#koreChatBot",
  ".chatWindowDiv",
  ".chatWindow",
  '[id*="chat"]',
  '[class*="chat-bot"]',
  '[class*="chatbot"]',
  ".kore-chat-bubble",
  '[class*="chatwindow"]',
  '[class*="droppable"]',
  '[class*="minimize"]',
  '[class*="cobrowse"]',
  '[data-testid*="chat"]',
  '[data-cy*="chat"]',
];

/**
 * @param {string} methodName
 * @param {...unknown} args
 * @returns {boolean}
 */
const invokeKoreSdkMethod = (methodName, ...args) => {
  if (!isClientSide() || !window?.KoreChatSDK) return false;
  try {
    const sdk = window.KoreChatSDK;
    const fn = sdk[methodName];
    if (typeof fn === "function") {
      fn.apply(sdk, args);
      return true;
    }
  } catch {
    /* Kore SDK threw — same as prior show/hide swallow */
  }
  return false;
};

/**
 * Opens the Kore widget the same way as fuel-cart proactive modal CTA (robust selectors).
 *
 * @returns {boolean} Whether a node was found and clicked.
 */
export const triggerChatbotClick = () => {
  if (!isClientSide()) return false;

  showKoreChatWindow();

  const minimizedEls = document.querySelectorAll('[class*="minimized"]');
  const minimizedMatch = Array.from(minimizedEls).find(
    (minimizedEl) =>
      minimizedEl instanceof HTMLElement &&
      minimizedEl.closest(
        '[id*="kore-chat-window"], [class*="kore-chat-window"]',
      ),
  );
  if (minimizedMatch) {
    minimizedMatch.click();
    trackKoreChatLauncherElementClicked();
    return true;
  }

  const preferredSelectors = [
    "#kore-chat-window .chat-circle",
    '[id*="kore-chat-window"] .chat-circle',
    ".kore-chat-button",
    "[data-kore-chatbot]",
    ".kore-chat-window",
    "#koreChatBot",
    ".chatWindowDiv",
    '[class*="chatbot"]',
    ...CHATBOT_CLICK_SELECTORS,
  ];

  const clickTarget = preferredSelectors
    .map((selector) => document.querySelector(selector))
    .find((el) => el instanceof HTMLElement);
  if (clickTarget) {
    clickTarget.click();
    trackKoreChatLauncherElementClicked();
    return true;
  }

  if (typeof window.KoreChatSDK?.showChat === "function") {
    try {
      window.KoreChatSDK.showChat();
    } catch {
      return false;
    }
    trackKoreChatLauncherElementClicked();
    return true;
  }

  return false;
};

/**
 * @param {Record<string, unknown>} config — position, proactive, trigger_type, page_type, ui_config (Kore API)
 * @returns {boolean}
 */
export const showChatIcon = (config) =>
  invokeKoreSdkMethod("showChatIcon", config);

/**
 * @returns {boolean}
 */
export const hideChatIcon = () => invokeKoreSdkMethod("hideChatIcon");

/**
 * Kore icon animation from Monarch `animate.message` (parity with fuel-cart `triggerAnimation`).
 *
 * @param {{ message?: string } | undefined} animateConfig
 * @returns {boolean}
 */
export const triggerAnimation = (animateConfig) => {
  if (!animateConfig || !isClientSide() || !window?.KoreChatSDK) {
    return false;
  }
  try {
    if (
      typeof window.KoreChatSDK.showMessageAnimationOnChatIcon === "function"
    ) {
      window.KoreChatSDK.showMessageAnimationOnChatIcon({
        trigger_type: "animate",
        animate: {
          message: animateConfig.message,
        },
      });
      return true;
    }
  } catch (error) {
    console.warn("Failed to trigger chat icon animation:", error);
  }
  return false;
};

export const extractChatUserId = () => {
  if (isClientSide() && window?.KoreChatSDK) {
    try {
      const userId =
        window.KoreChatSDK?.chatConfig?.jwtGrantSuccessInformation?.userInfo
          ?.userId;
      if (userId) {
        return userId;
      }
    } catch (error) {
      console.warn("Failed to extract chat user ID:", error);
    }
  }
  return null;
};
